import React, { Component } from 'react';
import { connect } from 'react-redux';
import './ThermostatAuthentication.css';
import { EllipsisLoader } from '../../../EllipsisLoader';
import { ThermostatActions } from '../../../../store';
import { Notification } from '../../../Notification';
import { GreyButton } from '../../../GreyButton';

export interface Props {
  clearErrors(): void;
  generatePinError?: string;
  handlePortalClick(): void;
  uid: string;
  pin?: string;
  onRequestClose(): void;
  onSuccess(): void;
  pollThermostat(uid: string): Promise<any>;
  refreshedPin?: string;
  refreshingPin: boolean;
  refreshPin(uid: string): void;
  thermostatError?: string;
  reauthenticate?: boolean;
}

export interface State {
  isPolling: boolean;
  timer: number;
}

export class ThermostatAuthentication extends Component<Props, State> {
  state = {
    isPolling: false,
    timer: 0,
  };

  input = React.createRef<HTMLInputElement>();

  componentWillUnmount() {
    this.stopTimer();
  }

  refreshPin = () => {
    const { uid, refreshPin } = this.props;
    refreshPin(uid);
  };

  copyToClipboard = () => {
    const { current } = this.input;
    if (current) {
      current.select();
      document.execCommand('copy');
    }
  };

  handleClick = () => {
    window.open('https://www.ecobee.com/home/ecobeeLogin.jsp', '_blank');
    const { clearErrors } = this.props;
    clearErrors();
    this.startTimer();
  };

  /*
   * Triggers a network request immediately when startTimer is called the
   * first time, then every x seconds after.
   *
   * This adds an isPolling variable to avoid multiple polls happening at
   * the same time. If a poll attempts while another poll is still active
   * (i.e. a long network request), it will simply drop that poll attempt.
   */

  startTimer = () => {
    const timeInterval = 30000;
    // Avoid starting another poll if our previous poll is still in progress, but try again in 5 seconds.
    if (this.state.isPolling) {
      this.setState(
        (state: State): State => ({
          ...state,
          timer: window.setTimeout(this.startTimer, timeInterval),
        }),
      );
      return;
    }

    // Start polling and poll again in x seconds
    this.setState(
      (state: State): State => ({
        ...state,
        isPolling: true,
        timer: window.setTimeout(this.startTimer, timeInterval),
      }),
      async () => {
        const { uid } = this.props;

        const result = await this.props.pollThermostat(uid).catch(() => 'error');

        if (!result || result === 'error') {
          this.stopTimer();
          return;
        }

        const {
          payload: { success, should_poll: shouldPoll },
        } = result;

        if (!shouldPoll && !success) {
          this.stopTimer();
          return;
        }

        if (!shouldPoll && success) {
          this.stopTimer();
          this.props.onSuccess();
          return;
        }

        this.setState(
          (state: State): State => ({
            ...state,
            isPolling: false,
          }),
        );
      },
    );
  };

  stopTimer = () => {
    window.clearTimeout(this.state.timer);
    this.setState(
      (state: State): State => ({
        ...state,
        isPolling: false,
        timer: 0,
      }),
    );
  };

  renderPin() {
    const { refreshingPin, refreshedPin, pin = '' } = this.props;
    const isIOS = navigator.userAgent.match(/ipad|ipod|iphone/i);

    return (
      <div className="thermostat-authentication__pin-div">
        <div className="thermostat-authentication__generated-pin-wrapper">
          {refreshingPin ? (
            <div
              className={`thermostat-authentication__generated-pin ${
                isIOS ? 'thermostat-authentication__generated-pin--ios' : ''
              }`}
            >
              <EllipsisLoader noBackground={true} text="" />
            </div>
          ) : (
            <input
              className={`thermostat-authentication__generated-pin ${
                isIOS ? 'thermostat-authentication__generated-pin--ios' : ''
              }`}
              type="text"
              value={refreshedPin || pin}
              ref={this.input}
              readOnly={true}
            />
          )}
        </div>
        {!isIOS && (
          <button className="thermostat-authentication__button-copy" onClick={this.copyToClipboard}>
            <div className="thermostat-authentication__button-text">Copy</div>
          </button>
        )}
      </div>
    );
  }

  render() {
    const { thermostatError, reauthenticate } = this.props;
    const showMessage = this.state.timer !== 0 || thermostatError;

    // handle iOS as a special case
    const message = reauthenticate
      ? 'Generate a new pin to reauthenticate. The pin is valid for 9 minutes.'
      : 'Use this pin to authenticate. The pin is valid for 9 minutes.';

    return (
      <div className="thermostat-authentication">
        <div className="thermostat-authentication__text-group">{message}</div>
        {reauthenticate && (
          <div className="thermostat-authentication__button--grey" id="refresh-pin">
            <GreyButton label="Generate New Pin" onClick={this.refreshPin} fullWidth={true} />
          </div>
        )}
        {this.renderPin()}
        {!reauthenticate && (
          <div className="thermostat-authentication__button--grey" id="refresh-pin">
            <GreyButton label="Refresh Pin" onClick={this.refreshPin} fullWidth={true} />
          </div>
        )}
        <div className="thermostat-authentication__text-group">Enter pin in Ecobee Portal.</div>
        <div className="thermostat-authentication__button--grey" id="ecobee-portal">
          <GreyButton label="Open Ecobee Portal" onClick={this.handleClick} fullWidth={true} />
        </div>
        <div
          className="thermostat-authentication__loading-message-div"
          style={showMessage ? { visibility: 'visible' } : { visibility: 'hidden' }}
        >
          {thermostatError ? (
            <span className="thermostat_authentication__message-div">
              <Notification message={thermostatError} type="error" />
              <button
                className="thermostat-authenitcation__button--try-again"
                type="button"
                id="thermostat-authentication__try-again"
                onClick={this.handleClick}
              >
                Try Again?
              </button>
            </span>
          ) : (
            <EllipsisLoader text="Waiting for pin verification" />
          )}
        </div>
      </div>
    );
  }
}

// TO DO: add type to state
export function mapStateToProps(state: any) {
  return {
    thermostatError: state.thermostatReducer.error,
    newRegisteredThermostat: state.thermostatReducer.newRegisteredThermostat,
    refreshingPin: state.thermostatReducer.refreshingPin,
    refreshedPin: state.thermostatReducer.refreshedPin,
  };
}

const mapDispatchToProps = {
  clearErrors: ThermostatActions.clearErrors,
  refreshPin: ThermostatActions.refreshPin,
  pollThermostat: ThermostatActions.pollThermostat,
};

const ConnectedThermostatAuthentication = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ThermostatAuthentication);
export { ConnectedThermostatAuthentication };
